import axiosClient, { resolveAssetUrl } from "../axiosClient";
import { User } from "../../types/common";

export interface LoginRequest {
  userName: string;
  password: string;
}

export interface SignupRequest {
  email: string;
  password: string;
  name: string;
}

export interface AuthResponse {
  user?: User; // Optional user object (for mock data)
  email?: string; // Email returned by backend
  token: string;
  type?: string; // Backend returns token type (e.g., "Bearer")
}

export const usersApi = {
  // Login user
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    try {
      const response = await axiosClient.post("/auth/login", {
        userName: credentials.userName,
        password: credentials.password,
      });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        const errorMessage =
          error.response.data?.message ||
          "Invalid credentials. Please check your username and password.";
        throw new Error(errorMessage);
      }
      console.error("Network error during login", error);
      throw new Error("Unable to connect to server. Please check your internet connection.");
    }
  },

  // Signup new user
  signup: async (userData: SignupRequest): Promise<AuthResponse> => {
    try {
      const emailPrefix = userData.email.split('@')[0];
      const response = await axiosClient.post('/auth/register', {
        email: userData.email,
        password: userData.password,
        userName: emailPrefix,
        fullName: userData.name || emailPrefix,
      });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        const errorMessage =
          error.response.data?.message ||
          'Registration failed. This email may already be registered.';
        throw new Error(errorMessage);
      }
      console.error('Network error during signup', error);
      throw new Error('Unable to connect to server. Please check your internet connection.');
    }
  },

  // Get current user profile
  getCurrentUser: async (): Promise<User> => {
    try {
      const response = await axiosClient.get("/users/me");
      const user = response.data;
      // Map backend fields to frontend interface
      return {
        ...user,
        name: user.fullName || user.userName || user.email,
        avatar: resolveAssetUrl(user.avatarUrl),
      };
    } catch (error: any) {
      console.error("Failed to fetch current user profile", error);
      throw error;
    }
  },

  // Update user profile
  updateProfile: async (updates: Partial<User>): Promise<User> => {
    const response = await axiosClient.patch("/users/me", updates);
    const user = response.data;
    return {
      ...user,
      name: user.fullName || user.userName || user.email,
      avatar: resolveAssetUrl(user.avatarUrl),
    };
  },

  // Upload/replace the current user's avatar. `blob` is the already-cropped
  // image produced client-side (see AvatarCropDialog).
  uploadAvatar: async (blob: Blob): Promise<User> => {
    const formData = new FormData();
    formData.append("file", blob, "avatar.jpg");
    const response = await axiosClient.post("/users/me/avatar", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const user = response.data;
    return {
      ...user,
      name: user.fullName || user.userName || user.email,
      avatar: resolveAssetUrl(user.avatarUrl),
    };
  },

  // Logout
  logout: async (): Promise<void> => {
    try {
      // Call backend logout endpoint to invalidate the session
      await axiosClient.post("/auth/logout");
    } catch (error) {
      // Even if logout fails on backend, we still clear local token
      console.warn(
        "Logout API call failed, but clearing local token anyway",
        error
      );
    } finally {
      // Always remove the token from localStorage
      localStorage.removeItem("authToken");
    }
  },

  // Get users by IDs (for displaying board members, task assignees, etc.)
  getUsersByIds: async (userIds: string[]): Promise<User[]> => {
    try {
      const response = await axiosClient.post("/users/by-ids", { userIds });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch users by IDs", error);
      throw error;
    }
  },

  // Search registered users by name/username/email - lets an admin add an
  // EXISTING account directly (as opposed to inviting someone who doesn't
  // have one yet). Backend requires >= 2 characters and caps at 10 results.
  searchUsers: async (
    query: string
  ): Promise<Array<{ id: number; fullName: string; userName: string; email: string }>> => {
    if (query.trim().length < 2) return [];
    const response = await axiosClient.get("/users/search", { params: { q: query } });
    return response.data;
  },
};
